import { useAuthContext } from '@/providers/AuthProvider';
import {
  startEmailCodeSignIn,
  startPhoneCodeSignIn,
  attemptSignInCode,
  type ClerkSignInStrategy,
} from '@/lib/clerk-auth-flows';
import { waitForClerkSessionResolution } from '@/lib/clerk-token';
import {
  ACCOUNT_LOCKED_MESSAGE,
  SESSION_VERIFY_FAILED_MESSAGE,
} from '@/providers/auth-context';
import { clearStaleAuthStorage } from '@/lib/auth-utils';

/**
 * REK-1986 (SSO.5): no-enumeration-style copy for the NEW phone tab. Phone
 * has no signup fallback on this surface (phone capture at signup is behind
 * a flag, default OFF), so an unknown number points back at the email flow —
 * mirroring the email copy pattern ("No account found… sign up first").
 */
export const PHONE_SIGNIN_NOT_FOUND_MESSAGE =
  'We couldn\u2019t sign you in with that phone number. Try your email instead, or sign up first.';

/**
 * How long the OTP verify paths wait for ClerkAuthProvider to resolve the
 * legacy identity after session activation. Existing users resolve in one
 * profiles query (sub-second); the timeout only guards pathological stalls.
 */
const CLERK_SIGN_IN_RESOLUTION_TIMEOUT_MS = 30_000;

/**
 * REK-2165 (SSO close-out Wave 2): the Supabase-engine email-OTP and
 * magic-link arms ran entirely through the `multi-email-auth` edge function,
 * which also owned the `is_locked` check before it minted a session. With that
 * function retired there is no lock-safe path left, so those arms fail closed
 * here instead of falling back to a bare `supabase.auth.*` call that would hand
 * a locked account a session. The whole Supabase engine goes with REK-2166.
 *
 * The copy is deliberately generic: it must not reveal whether an address has
 * an account (REK-1732), and it is reused by the signup arm in `useFastAuth`.
 */
export const SUPABASE_EMAIL_OTP_RETIRED_MESSAGE =
  'Email code sign-in is unavailable in this build. Please try again later.';

/** `Error.name` the retired arms set, so callers can branch without matching copy. */
export const SUPABASE_EMAIL_OTP_RETIRED_ERROR_NAME = 'SupabaseEmailOtpRetiredError';
export function retiredSupabaseEmailOtpError(): Error {
  const err = new Error(SUPABASE_EMAIL_OTP_RETIRED_MESSAGE);
  err.name = SUPABASE_EMAIL_OTP_RETIRED_ERROR_NAME;
  return err;
}

/**
 * `sendMagicLink` success payload. Every field is optional because the shape
 * depends on the flow outcome: consumer sends resolve to
 * `{ userExists, verifyEmail }` (or `{ userExists: false }`), while the
 * admin-only path (`signUpIfMissing: false`) can additionally resolve to
 * `{ alreadyAdmin: true }` (active admin session — no code sent) or
 * `{ switchAccount: true, signedInAs }` (a non-admin session was signed out —
 * the caller stays put and renders switch-account copy).
 */
export interface SendMagicLinkData {
  userExists?: boolean;
  verifyEmail?: string;
  alreadyAdmin?: boolean;
  switchAccount?: boolean;
  signedInAs?: string | null;
}

/**
 * Auth hook that wraps AuthProvider context and adds sign-in/sign-up methods.
 * All auth state comes from the single AuthProvider - no duplicate listeners.
 * Clerk is the sole engine (SSO Wave 3, REK-2166).
 */
export function useAuth() {
  const context = useAuthContext();

  /**
   * REK-1986 (SSO.5): shared Clerk OTP verification for the email and phone
   * sign-in flows. Attempts the prepared first factor, then waits for
   * ClerkAuthProvider to resolve the legacy identity so the caller can
   * navigate only once the session is genuinely granted (locked accounts and
   * fail-closed lookups surface here with the existing copy).
   */
  const verifyClerkSignInCode = async (
    strategy: ClerkSignInStrategy,
    code: string,
    safeIdentifier?: string,
  ) => {
    const result = await attemptSignInCode(
      strategy,
      code,
      safeIdentifier ? { safeIdentifier } : undefined,
    );
    if (result.status === 'error') {
      return {
        data: null,
        error: new Error(result.error.message),
        accountLocked: result.error.accountLocked,
      };
    }
    // New-user path (email_code + signUpIfMissing): the first-factor verification
    // lands as 'transferable' — the session has NOT been activated yet, so calling
    // waitForClerkSessionResolution here would time-out (~100 s) or return
    // session-verify-failed. Return immediately; the caller is responsible for
    // invoking completeEmailSignUpTransfer and then navigating. (Finding #16,
    // REK-2167 Step 3a)
    // This guard is also safe for the phone-code strategy: phone never receives
    // signUpIfMissing so it never produces a 'transferable' result (Invariant #4).
    if (result.status === 'transferable') {
      return {
        data: { status: 'transferable' as const, safeIdentifier: result.safeIdentifier },
        error: null,
        accountLocked: false,
      };
    }
    const resolution = await waitForClerkSessionResolution(
      CLERK_SIGN_IN_RESOLUTION_TIMEOUT_MS,
    );
    if (resolution === 'granted') {
      return { data: { success: true }, error: null, accountLocked: false };
    }
    if (resolution === 'denied-locked') {
      return { data: null, error: new Error(ACCOUNT_LOCKED_MESSAGE), accountLocked: true };
    }
    return { data: null, error: new Error(SESSION_VERIFY_FAILED_MESSAGE), accountLocked: false };
  };

  /**
   * @deprecated The Supabase OTP send path is retired (REK-2165). Use
   * `sendMagicLink` (which sends a Clerk email code) instead.
   */
  const sendOtp = async (_email: string, _shouldCreateUser = true) => {
    // REK-2165: fail closed — no caller can reach a lock-bypassing path
    // through the deprecated alias.
    return { data: null, error: retiredSupabaseEmailOtpError() };
  };

  const verifyOtp = async (email: string, token: string) => {
    return verifyClerkSignInCode('email_code', token, email);
  };

  const sendMagicLink = async (
    email: string,
    _next?: string,
    options?: { signUpIfMissing?: boolean },
  ) => {
    // REK-1986 (SSO.5): under Clerk this starts the email_code custom flow —
    // the same email → "Check your email" → 6-digit code UX, minus the magic
    // link (D-2: Clerk's email carries a code only). The `next` path is
    // unused (no emailed redirect); the caller navigates after verify.
    // REK-1732: Clerk form_identifier_exists / user_not_found remains a
    // Deferred residual surface.
    // REK-2167: consumer login defaults signUpIfMissing on; admin login must
    // pass `{ signUpIfMissing: false }` so unknown emails stay enumeration-neutral.
    const result = await startEmailCodeSignIn(email, options);
    if (result.status === 'sent') {
      const data: SendMagicLinkData = { userExists: true, verifyEmail: result.verifyIdentifier };
      return {
        data,
        error: null,
        rateLimited: false,
        retryAfter: null,
        accountLocked: false,
      };
    }
    if (result.status === 'user_not_found') {
      const data: SendMagicLinkData = { userExists: false };
      return { data, error: null, rateLimited: false, retryAfter: null, accountLocked: false };
    }
    // Admin-only path (`signUpIfMissing: false`): the flow transport resolved
    // the pre-existing session without sending a code. Forwarded without
    // disturbing the consumer `{ userExists, verifyEmail }` shape above.
    if (result.status === 'already_signed_in_admin') {
      const data: SendMagicLinkData = { alreadyAdmin: true };
      return { data, error: null, rateLimited: false, retryAfter: null, accountLocked: false };
    }
    if (result.status === 'switch_account') {
      const data: SendMagicLinkData = { switchAccount: true, signedInAs: result.signedInAs };
      return { data, error: null, rateLimited: false, retryAfter: null, accountLocked: false };
    }
    return {
      data: null,
      error: new Error(result.error.message),
      rateLimited: result.error.rateLimited,
      retryAfter: result.error.retryAfter,
      accountLocked: false,
    };
  };

  /**
   * REK-1986 (SSO.5): phone-login path (Clerk custom flows only). Sends the
   * SMS OTP for an E.164 number.
   */
  const sendPhoneOtp = async (phoneE164: string) => {
    const result = await startPhoneCodeSignIn(phoneE164);
    if (result.status === 'sent') {
      return {
        data: { verifyIdentifier: result.verifyIdentifier },
        error: null,
        rateLimited: false,
        retryAfter: null,
      };
    }
    if (result.status === 'user_not_found') {
      return {
        data: null,
        error: new Error(PHONE_SIGNIN_NOT_FOUND_MESSAGE),
        rateLimited: false,
        retryAfter: null,
      };
    }
    return {
      data: null,
      error: new Error(result.error.message),
      rateLimited: result.error.rateLimited,
      retryAfter: result.error.retryAfter,
    };
  };

  /** REK-1986 (SSO.5): verify the SMS OTP sent by `sendPhoneOtp`. */
  const verifyPhoneOtp = async (code: string) => {
    return verifyClerkSignInCode('phone_code', code);
  };

  const signOut = async () => {
    clearStaleAuthStorage();
    await context.signOut();
    return { error: null };
  };

  return {
    ...context,
    sendMagicLink,
    sendPhoneOtp,
    verifyPhoneOtp,
    signOut,
    /** @deprecated Use `sendMagicLink` instead. */
    sendOtp,
    verifyOtp,
  };
}
